import type { ReactNode } from 'react';

import { useLocale } from '@/i18n/context';
import { LOCALES } from '@/i18n/locale';

/** Each language written in itself, so it is legible to whoever wants it. */
const LANGUAGE_NAMES = { en: 'EN', ru: 'RU' };

interface AuthScreenProps {
  title: string;
  subtitle?: string;
  /** Non-field error from the last submit, e.g. bad credentials or throttling. */
  error?: string | null;
  /** Something that went right, e.g. an address that has just been confirmed. */
  notice?: string | null;
  children: ReactNode;
  footer?: ReactNode;
}

/** Shared chrome for the four credential screens. */
export function AuthScreen({
  title,
  subtitle,
  error,
  notice,
  children,
  footer,
}: AuthScreenProps) {
  const { locale, setLocale } = useLocale();

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm opacity-60">{subtitle}</p>}
      </header>

      {error && (
        <p role="alert" className="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
          {error}
        </p>
      )}

      {/* `status` rather than `alert`: good news should be announced when the
          reader gets to it, not interrupt whatever they are already hearing. */}
      {notice && (
        <p role="status" className="rounded-lg bg-current/5 p-4 text-sm">
          {notice}
        </p>
      )}

      {children}

      {footer && <div className="text-sm opacity-70">{footer}</div>}

      {/* Lives on the shared chrome rather than on each screen: these four are
          the only pages a visitor with no account can reach, and someone whose
          browser is set to the wrong language needs a way out of it. */}
      <div className="flex justify-center gap-3 text-xs">
        {LOCALES.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setLocale(option)}
            aria-pressed={locale === option}
            className={locale === option ? 'font-medium' : 'underline opacity-50'}
          >
            {LANGUAGE_NAMES[option]}
          </button>
        ))}
      </div>
    </main>
  );
}

export function SubmitButton({
  children,
  pending,
}: {
  children: ReactNode;
  pending: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="rounded-full bg-current/10 px-6 py-3.5 font-medium disabled:opacity-40"
    >
      {children}
    </button>
  );
}
